import { Subcription } from './namespace';
import { AccessState } from './translation_namespace/access_namespace';
import { TeamMemberRole } from './translation_namespace/professional_namespace';

/**
 * Niveau d'une session d'impersonation admin (prise de contrôle d'un compte pro).
 * Le token garde le rôle réel du pro dans les deux cas.
 * - READ_ONLY : toutes les lectures du pro, aucune écriture (`ImpersonationReadonlyGuard` côté
 *   API, commandes masquées côté interface via `usePermissions`).
 * - FULL : droits complets pour corriger le compte.
 */
export enum ImpersonationScope {
  READ_ONLY = 'read_only',
  FULL = 'full',
}

/**
 * Comment la session a été ouverte. Absent = mot de passe (sessions historiques).
 * `LINK` = lien de reprise d'essai (email J0 → J7, sans mot de passe) : la session est
 * complète pour configurer le compte, mais les actions sensibles (moyen de paiement,
 * export de la base clients) redemandent une authentification par mot de passe via
 * `@RequirePasswordAuth()` côté backend.
 */
export enum AuthMethod {
  PASSWORD = 'password',
  LINK = 'link',
}

export class TokenSession {
  pro_id!: number;
  account_id!: number;
  team_member_id!: number | null;
  role!: TeamMemberRole;
  subscription!: Subcription;
  locale!: string;
  timezone!: string;
  // Gating d'accès (Volet A). `access_level` n'est PAS stocké : il se dérive de
  // (access_state, payment_at_risk) via computeAccessLevel(), back comme front.
  access_state!: AccessState;
  payment_at_risk!: boolean;
  trial_end_date!: string | null; // ISO, pour le compte à rebours frontend
  // Gating d'entitlement (Volet B) — flags addon portés dans le JWT pour gater une option
  // côté front sans appel /me. Le gating par plan reste piloté par `subscription` + scopes.
  is_smart_ride!: boolean;
  is_white_label!: boolean;
  is_mobile_app_whitelabel!: boolean;
  // Pack SEO ouvert pour ce compte (test sur quelques pros avant l'ouverture générale), quel que
  // soit le plan : le plan MAX reste vérifié à part par le scope `website.seo_articles`.
  is_seo_articles!: boolean;
  // Dérogation admin : pack SEO ouvert SANS la formule MAX. Ajoute le scope `website.seo_articles`
  // aux scopes de la formule (`resolveScopes(..., scopeGrantsOf(token))`). Absent = MAX exigé.
  is_seo_articles_any_plan?: boolean;
  // Impersonation (prise de contrôle admin). Absents sur les sessions pro normales.
  // `impersonation_scope` porte l'enforcement lecture seule (`ImpersonationReadonlyGuard`, le
  // `role` reste celui du pro) ; les trois servent aussi au bandeau, à l'audit et à l'UI.
  // `impersonated_by` = admin_user_id de l'admin acteur.
  impersonated_by?: number;
  impersonation_scope?: ImpersonationScope;
  impersonator_label?: string;
  // Mode d'ouverture de la session (cf. AuthMethod). Porté par le refresh token aussi,
  // pour survivre aux rotations : une session « lien » le reste jusqu'au prochain login.
  auth_method?: AuthMethod;
  // Jeton de tour Pulse (lot 5 onboarding v4) : la conversation qui porte l'écriture. Posé par
  // le runtime sur le jeton serveur des appels d'outils, jamais sur une session navigateur.
  // Les services d'écriture le lisent pour tracer `origin = pulse` (cf. pulseOriginFromSession).
  pulse_conversation_id?: number;
}
